import {
  GLOBAL_ROLE_PERMISSIONS,
  type Permission,
  type GlobalRole,
  ProjectRole,
  type UserStatus,
  UserStatus as Status,
} from '@k-ucap/shared';

export interface UserContext {
  uid: string;
  globalRole: GlobalRole;
  status: UserStatus;
  projectRoles: Map<string, ProjectRole>;
}

const DEMO_INTERACT_PERMISSIONS: Permission[] = [
  'MESSAGE_GENERATE' as Permission,
  'MESSAGE_ANALYZE' as Permission,
  'SCENARIO_VIEW' as Permission,
  'SCENARIO_RUN' as Permission,
  'GIS_VIEW' as Permission,
  'MESSAGE_EVENT_VIEW' as Permission,
  'REPORT_VIEW' as Permission,
];

export interface PermissionServiceOptions {
  isDemoProject?: boolean;
}

export class PermissionService {
  private readonly userContext: UserContext | null;
  private readonly permissions: Set<Permission>;
  private readonly isDemoProject: boolean;

  constructor(userContext: UserContext | null, options: PermissionServiceOptions = {}) {
    this.userContext = userContext;
    this.permissions = new Set(userContext ? (GLOBAL_ROLE_PERMISSIONS[userContext.globalRole] ?? []) : []);
    this.isDemoProject = options.isDemoProject ?? false;
  }

  hasPermission(permission: Permission): boolean {
    if (!this.userContext) return false;
    if (this.userContext.status !== Status.ACTIVE) return false;
    if (this.permissions.has(permission)) return true;
    if (
      this.isDemoProject &&
      this.permissions.has('DEMO_INTERACT' as Permission) &&
      DEMO_INTERACT_PERMISSIONS.includes(permission)
    ) {
      return true;
    }
    return false;
  }

  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }

  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every((p) => this.hasPermission(p));
  }

  canAccessProject(projectId: string, projectRoleRequired?: ProjectRole): boolean {
    if (!this.userContext) return false;
    if (this.userContext.status !== Status.ACTIVE) return false;
    if (this.userContext.globalRole === 'ADMIN') return true;
    if (!projectRoleRequired) return this.userContext.projectRoles.has(projectId);
    const role = this.userContext.projectRoles.get(projectId);
    if (!role) return false;
    const hierarchy = { [ProjectRole.VIEWER]: 0, [ProjectRole.OPERATOR]: 1, [ProjectRole.PROJECT_ADMIN]: 2 };
    return hierarchy[role] >= hierarchy[projectRoleRequired];
  }

  canManageProject(projectId: string): boolean {
    return this.canAccessProject(projectId, ProjectRole.PROJECT_ADMIN);
  }

  canOperateProject(projectId: string): boolean {
    return this.canAccessProject(projectId, ProjectRole.OPERATOR);
  }

  canGenerateMessage(): boolean {
    return this.hasPermission('MESSAGE_GENERATE' as Permission);
  }

  canRunScenario(): boolean {
    return this.hasPermission('SCENARIO_RUN' as Permission);
  }

  canManageUsers(): boolean {
    return this.hasPermission('USER_MANAGE' as Permission);
  }

  resolvePermissions(): Permission[] {
    return Array.from(this.permissions);
  }
}

export const createPermissionService = (
  userContext: UserContext | null,
  options?: PermissionServiceOptions
): PermissionService => new PermissionService(userContext, options);
